import * as attribution from "./attribution";
import { allowed } from "./consent";
import { PAYLOAD_WHITELIST } from "./events";
import uuid from "./uuid";
import meta from "./providers/meta";
import google from "./providers/google";
import tiktok from "./providers/tiktok";
import linkedin from "./providers/linkedin";
import gtm from "./providers/gtm";

export * from "./events";

/**
 * Tracking de campanhas do portal (agnóstico de plataforma).
 *
 * Regras:
 * - Só usa a configuração do portal atual (bloco "marketing" de /backend/company/config).
 * - Payload restrito à whitelist (PAYLOAD_WHITELIST), sem PII e sem leitura de DOM.
 * - Provider só inicializa/envia com o consentimento da categoria exigida.
 * - Nunca lança erro e nunca bloqueia a navegação ou a candidatura.
 */
// gtm primeiro: o Consent Mode default precisa entrar no dataLayer antes das tags do Google
const PROVIDERS = { gtm, meta, google, tiktok, linkedin };
const MAX_VALUE_LENGTH = 150;

const state = {
    config: null,
    portalId: null,
    active: {},
    // Providers cujo script já foi injetado (continua carregado após revogação; recebe as atualizações de consentimento)
    loaded: {},
    allowed,
};

function debug(error) {
    try {
        console.debug("[tracking]", error);
    } catch (e) {
        // sem console
    }
}

function providerConfig(name) {
    return state.config && state.config[name] ? state.config[name] : null;
}

/**
 * (Re)avalia o consentimento: inicializa providers recém-autorizados e desliga os revogados.
 * O script já injetado só some no próximo carregamento; desligado, ele não recebe mais eventos.
 */
export function onConsentChange() {
    try {
        Object.keys(PROVIDERS).forEach((name) => {
            const config = providerConfig(name);
            if (!config) {
                return;
            }

            const isAllowed = PROVIDERS[name].isAllowed(config, state.allowed);
            if (isAllowed && !state.loaded[name]) {
                try {
                    PROVIDERS[name].init(config, state.allowed);
                    state.loaded[name] = true;
                } catch (e) {
                    debug(e);
                }
            } else if (state.loaded[name] && typeof PROVIDERS[name].consentChanged === "function") {
                try {
                    PROVIDERS[name].consentChanged(config, state.allowed);
                } catch (e) {
                    debug(e);
                }
            }
            state.active[name] = isAllowed && !!state.loaded[name];
        });

        attribution.flushPending();
    } catch (e) {
        debug(e);
    }
}

export function installTracking(companyConfig, options = {}) {
    try {
        state.config = (companyConfig && companyConfig.marketing) || {};
        state.portalId = companyConfig ? companyConfig.instance_id : null;
        state.active = {};
        state.loaded = {};
        if (typeof options.allowed === "function") {
            state.allowed = options.allowed;
        }

        attribution.capture();
        onConsentChange();
    } catch (e) {
        debug(e);
    }
}

function cleanValue(value) {
    if (value === null || value === undefined || value === "") {
        return undefined;
    }

    return typeof value === "number" ? value : String(value).slice(0, MAX_VALUE_LENGTH);
}

/**
 * Monta o payload somente com as chaves permitidas. Qualquer outra chave de "data" é descartada.
 */
export function buildPayload(eventName, eventId, data = {}) {
    const current = attribution.get();
    const last = current ? current.last : {};

    const candidate = {
        event_name: eventName,
        event_id: eventId,
        job_id: data.job_id,
        job_title: data.job_title,
        portal_id: state.portalId,
        source: last.utm_source,
        medium: last.utm_medium,
        campaign: last.utm_campaign,
    };

    return PAYLOAD_WHITELIST.reduce((payload, key) => {
        const value = cleanValue(candidate[key]);
        if (value !== undefined) {
            payload[key] = value;
        }
        return payload;
    }, {});
}

/**
 * Dispara um evento para os providers ativos do portal atual. Retorna o event_id na hora (síncrono);
 * o envio acontece depois, fora do fluxo do usuário.
 */
export function track(eventName, data = {}) {
    const eventId = (data && data.event_id) || uuid();

    try {
        if (!state.config) {
            return eventId;
        }

        const payload = buildPayload(eventName, eventId, data);

        Object.keys(PROVIDERS).forEach((name) => {
            const config = providerConfig(name);
            if (!config || !state.active[name]) {
                return;
            }

            setTimeout(() => {
                try {
                    if (PROVIDERS[name].isAllowed(config, state.allowed)) {
                        PROVIDERS[name].send(eventName, payload, config, state.allowed);
                    }
                } catch (e) {
                    debug(e);
                }
            }, 0);
        });
    } catch (e) {
        debug(e);
    }

    return eventId;
}

/**
 * Dispara no máximo uma vez por sessão do navegador para a chave informada (evita duplicidade).
 */
export function trackOnce(onceKey, eventName, data = {}) {
    const storageKey = `trk:${onceKey}`;

    try {
        if (window.sessionStorage.getItem(storageKey)) {
            return null;
        }
        window.sessionStorage.setItem(storageKey, "1");
    } catch (e) {
        // sem sessionStorage: dispara mesmo assim
    }

    return track(eventName, data);
}

/**
 * Corpo enviado no POST de candidatura: atribuição vigente + event_id do application_complete.
 */
export function enrollPayload(eventId) {
    try {
        const current = attribution.get();

        return {
            event_id: eventId,
            instance_id: state.portalId,
            ...(current ? current.last : {}),
            touches: current ? current.touches : [],
        };
    } catch (e) {
        debug(e);
        return null;
    }
}

export function captureAttribution() {
    attribution.capture();
}

export { uuid };

export function resetForTests() {
    state.config = null;
    state.portalId = null;
    state.active = {};
    state.loaded = {};
    state.allowed = allowed;
}
